import type { Marker, Overlay } from '../../domain/annotation';

/**
 * Der Bearbeitungszustand der Markierungsebene.
 *
 * Eigene, React-freie Datei: Undo, Redo und die Auswahl sind der Teil, an dem sich still etwas
 * verlieren kann, und sie sollen ohne DOM prüfbar bleiben. Die Grossansicht hält den Zustand
 * mit `useReducer` und ruft hier nur hinein.
 */

export interface EditorState {
  overlay: Overlay;
  /** Kennung der ausgewählten Marke, oder `null`. */
  auswahl: string | null;
  /** Stände **vor** der jeweiligen Änderung, jüngster zuletzt. */
  verlauf: Overlay[];
  /** Durch Undo zurückgelegte Stände, jüngster zuletzt. */
  zukunft: Overlay[];
  /**
   * Läuft gerade ein Ziehen (Verschieben oder Skalieren)?
   *
   * Während eines Zuges kommt bei **jeder** Zeigerbewegung eine Änderung herein. Ohne diese
   * Klammer bekäme jede davon einen eigenen Verlaufseintrag, und ein Undo nähme einen
   * einzigen Pixel zurück — um ein Verschieben rückgängig zu machen, müsste der Prüfer
   * vierzigmal tippen.
   */
  imZug: boolean;
}

export type EditorAction =
  /** Den geladenen Stand einsetzen — der Verlauf beginnt damit von vorn. */
  | { typ: 'ersetzen'; overlay: Overlay }
  /** Beginn eines Ziehens: **ein** Verlaufseintrag für den ganzen Zug. */
  | { typ: 'zug-start' }
  /** Ende eines Ziehens. Hat sich nichts bewegt, verschwindet der Eintrag wieder. */
  | { typ: 'zug-ende' }
  | { typ: 'marke-hinzu'; marke: Marker }
  | { typ: 'marke-aendern'; marke: Marker }
  | { typ: 'marke-loeschen'; id: string }
  | { typ: 'auswaehlen'; id: string | null }
  | { typ: 'alles-loeschen' }
  | { typ: 'undo' }
  | { typ: 'redo' };

export const startZustand = (overlay: Overlay): EditorState => ({
  overlay,
  auswahl: null,
  verlauf: [],
  zukunft: [],
  imZug: false,
});

/**
 * Die nächste freie Nummer für eine nummerierte Marke: **eins über der höchsten je
 * vergebenen**, nicht die kleinste freie.
 *
 * Wird die 2 von 1, 2, 3 gelöscht, heisst die nächste 4. Nähme sie die 2 wieder auf, gäbe es
 * im Kommentartext des Prüfers zwei Verweise „2" auf verschiedene Stellen im Bild.
 */
export const naechsteNummer = (markers: Marker[]): number =>
  markers.reduce((groesste, m) => (m.kind === 'number' ? Math.max(groesste, m.n) : groesste), 0) + 1;

const gleich = (a: Overlay, b: Overlay) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Eine inhaltliche Änderung: der bisherige Stand wandert in den Verlauf, die Zukunft fällt weg.
 *
 * **Innerhalb eines Zuges nicht:** dort liegt der Eintrag schon von `zug-start` vor, und jede
 * weitere Zeigerbewegung überschreibt nur den Stand.
 */
function aendere(state: EditorState, neu: Overlay, auswahl = state.auswahl): EditorState {
  if (state.imZug) return { ...state, overlay: neu, auswahl };
  return {
    ...state,
    overlay: neu,
    auswahl,
    verlauf: [...state.verlauf, state.overlay],
    // Nach einer neuen Änderung darf kein Redo mehr in den verlassenen Zweig führen — sonst
    // erschiene eine Marke wieder, die zu einer anderen Bearbeitungsgeschichte gehört.
    zukunft: [],
  };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.typ) {
    case 'zug-start':
      // Der Stand **vor** dem Zug kommt hier in den Verlauf, ein einziges Mal.
      return { ...state, imZug: true, verlauf: [...state.verlauf, state.overlay], zukunft: [] };

    case 'zug-ende': {
      const vorherigerStand = state.verlauf[state.verlauf.length - 1];
      // Ein blosses Antippen ohne Bewegung ist keine Änderung. Bliebe der Eintrag stehen,
      // täte das nächste Undo scheinbar nichts — der Prüfer tippte weiter und verlöre dann
      // eine echte Änderung.
      const ohneBewegung = vorherigerStand !== undefined && gleich(vorherigerStand, state.overlay);
      return {
        ...state,
        imZug: false,
        verlauf: ohneBewegung ? state.verlauf.slice(0, -1) : state.verlauf,
      };
    }

    case 'ersetzen':
      // Kein Verlaufseintrag: das Laden ist keine Änderung des Prüfers. Stünde es im
      // Verlauf, führte ein einziges Undo auf eine leere Ebene, und das Schliessen danach
      // löschte die Datei.
      return startZustand(action.overlay);

    case 'marke-hinzu':
      return aendere(
        state,
        { ...state.overlay, markers: [...state.overlay.markers, action.marke] },
        action.marke.id,
      );

    case 'marke-aendern':
      // Ersetzen **an Ort und Stelle**: würde die geänderte Marke ans Ende wandern, spränge
      // sie beim blossen Verschieben vor eine Deckfläche, die sie eben noch verdeckt hat.
      return aendere(state, {
        ...state.overlay,
        markers: state.overlay.markers.map((m) => (m.id === action.marke.id ? action.marke : m)),
      });

    case 'marke-loeschen':
      return aendere(
        state,
        { ...state.overlay, markers: state.overlay.markers.filter((m) => m.id !== action.id) },
        state.auswahl === action.id ? null : state.auswahl,
      );

    case 'alles-loeschen':
      // Ein einziger Verlaufsschritt: zwei Dutzend Marken einzeln zurückholen zu müssen wäre
      // ein Fehlgriff, der sich kaum heilen lässt.
      return aendere(state, { ...state.overlay, markers: [] }, null);

    case 'auswaehlen':
      // Kein Verlaufseintrag — eine Auswahl ändert die Ebene nicht. Stünde sie im Verlauf,
      // müsste der Prüfer mehrmals Undo tippen, bevor überhaupt eine Marke verschwindet.
      return { ...state, auswahl: action.id };

    case 'undo': {
      const vorher = state.verlauf[state.verlauf.length - 1];
      if (vorher === undefined) return state;
      return {
        ...state,
        overlay: vorher,
        auswahl: null,
        verlauf: state.verlauf.slice(0, -1),
        zukunft: [...state.zukunft, state.overlay],
      };
    }

    case 'redo': {
      const wieder = state.zukunft[state.zukunft.length - 1];
      if (wieder === undefined) return state;
      return {
        ...state,
        overlay: wieder,
        auswahl: null,
        verlauf: [...state.verlauf, state.overlay],
        zukunft: state.zukunft.slice(0, -1),
      };
    }
  }
}
